/**
 * Form Runtime CSS — Default styles for prm-* components
 *
 * All styles use CSS custom properties from the platform theme (--prm-*)
 * so they automatically adapt to light/dark themes and can be overridden
 * by the developer via class or style attributes without conflict.
 */

export const serializeFormStylesForRuntime = (): string => {
    return `
/* ══════════════════════════════════════════════════════
   PRM Form Runtime — Default Styles
   All values derive from platform theme variables.
   Override freely via class, style, or style_code.
   ══════════════════════════════════════════════════════ */

/* ── Form ── */
.prm-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: 800px;
    margin: 0 auto;
    padding: 24px;
    font-family: inherit;
}

/* ── Stepper (multi-step) ── */
.prm-stepper {
    display: flex;
    gap: 4px;
    margin-bottom: 24px;
    padding: 0 0 16px;
    border-bottom: 1px solid var(--prm-border, #e5e7eb);
}

.prm-stepper__step {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: var(--prm-radius, 8px);
    font-size: 0.875rem;
    color: var(--prm-text-secondary, #6b7280);
    transition: all 0.2s ease;
    flex: 1;
}

.prm-stepper__step--active {
    background: var(--prm-primary, #3b82f6);
    color: var(--prm-primary-contrast, #ffffff);
    font-weight: 600;
}

.prm-stepper__step--completed {
    color: var(--prm-primary, #3b82f6);
}

.prm-stepper__number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--prm-surface, #f9fafb);
    border: 2px solid var(--prm-border, #e5e7eb);
    font-weight: 600;
    font-size: 0.8rem;
    flex-shrink: 0;
}

.prm-stepper__step--active .prm-stepper__number {
    background: var(--prm-primary-contrast, #ffffff);
    color: var(--prm-primary, #3b82f6);
    border-color: var(--prm-primary-contrast, #ffffff);
}

.prm-stepper__step--completed .prm-stepper__number {
    background: var(--prm-primary, #3b82f6);
    color: var(--prm-primary-contrast, #ffffff);
    border-color: var(--prm-primary, #3b82f6);
}

.prm-stepper__title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ── Step ── */
.prm-step {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

/* ── Section ── */
.prm-section {
    border: 1px solid var(--prm-border, #e5e7eb);
    border-radius: var(--prm-radius, 8px);
    padding: 16px;
    margin: 0;
    background: var(--prm-surface, #ffffff);
}

.prm-section__legend {
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--prm-text-primary, #1f2937);
    cursor: default;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 4px;
}

.prm-section__legend[onclick] { cursor: pointer; }

.prm-section__toggle {
    font-size: 0.75rem;
    color: var(--prm-text-secondary, #6b7280);
}

.prm-section__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 12px;
}

/* ── Row ── */
.prm-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

/* ── Field wrapper ── */
.prm-field-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

/* ── Field label ── */
.prm-field__label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--prm-text-secondary, #6b7280);
    line-height: 1.4;
}

.prm-field__required {
    color: var(--prm-error-on-surface, #B93C0E);
}

/* ── Field input ── */
.prm-field__input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--prm-border-input, #6B7280);
    border-radius: var(--prm-radius, 6px);
    background: var(--prm-surface, #ffffff);
    color: var(--prm-text-primary, #1f2937);
    font-size: 0.9375rem;
    font-family: inherit;
    line-height: 1.5;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    box-sizing: border-box;
    outline: none;
}

.prm-field__input:focus {
    border-color: var(--prm-primary, #3b82f6);
    box-shadow: 0 0 0 3px var(--prm-focus, rgba(59, 130, 246, 0.15));
}

.prm-field__input::placeholder {
    color: var(--prm-text-disabled, #9ca3af);
}

.prm-field__input:disabled,
.prm-field__input[readonly] {
    background: var(--prm-bg, #f3f4f6);
    color: var(--prm-text-disabled, #9ca3af);
    cursor: not-allowed;
}

.prm-field--error .prm-field__input {
    border-color: var(--prm-error-on-surface, #B93C0E);
}

.prm-field--error .prm-field__input:focus {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15);
}

/* Native date/time inputs draw their own calendar/clock icon; browsers only
   offer a light-or-dark choice for it (via color-scheme), not real theming.
   mask-image + background-color recolors it exactly to the input's own text
   color instead, so it always matches regardless of theme. WebKit/Blink only
   (Firefox has no equivalent pseudo-element) — falls back to the browser's
   default icon there, same as before. */
.prm-field__input[type="date"]::-webkit-calendar-picker-indicator,
.prm-field__input[type="datetime-local"]::-webkit-calendar-picker-indicator,
.prm-field__input[type="time"]::-webkit-calendar-picker-indicator {
    background: none;
    background-color: var(--prm-text-primary, #1f2937);
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
    -webkit-mask-size: contain;
    mask-size: contain;
    width: 16px;
    height: 16px;
    cursor: pointer;
}

.prm-field__input[type="date"]::-webkit-calendar-picker-indicator,
.prm-field__input[type="datetime-local"]::-webkit-calendar-picker-indicator {
    -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='black'%3E%3Cpath fill-rule='evenodd' d='M5.75 2a.75.75 0 0 1 .75.75V4h7V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 18 6.75v8.5A2.75 2.75 0 0 1 15.25 18H4.75A2.75 2.75 0 0 1 2 15.25v-8.5A2.75 2.75 0 0 1 4.75 4H5V2.75A.75.75 0 0 1 5.75 2Zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75Z' clip-rule='evenodd'/%3E%3C/svg%3E");
    mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='black'%3E%3Cpath fill-rule='evenodd' d='M5.75 2a.75.75 0 0 1 .75.75V4h7V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 18 6.75v8.5A2.75 2.75 0 0 1 15.25 18H4.75A2.75 2.75 0 0 1 2 15.25v-8.5A2.75 2.75 0 0 1 4.75 4H5V2.75A.75.75 0 0 1 5.75 2Zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75Z' clip-rule='evenodd'/%3E%3C/svg%3E");
}

.prm-field__input[type="time"]::-webkit-calendar-picker-indicator {
    -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='black'%3E%3Cpath fill-rule='evenodd' d='M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z' clip-rule='evenodd'/%3E%3C/svg%3E");
    mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='black'%3E%3Cpath fill-rule='evenodd' d='M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z' clip-rule='evenodd'/%3E%3C/svg%3E");
}

/* ── Textarea ── */
.prm-field__textarea {
    resize: vertical;
    min-height: 80px;
}

/* ── Select ── */
.prm-field__select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8.825a.5.5 0 0 1-.354-.146l-4-4a.5.5 0 0 1 .708-.708L6 7.617l3.646-3.646a.5.5 0 0 1 .708.708l-4 4A.5.5 0 0 1 6 8.825z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    padding-right: 36px;
    cursor: pointer;
}

/* ── Boolean (checkbox) ── */
.prm-field--boolean {
    flex-direction: row;
    align-items: center;
}

.prm-field__checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 0.9375rem;
    color: var(--prm-text-primary, #1f2937);
}

.prm-field__checkbox {
    width: 18px;
    height: 18px;
    accent-color: var(--prm-primary, #3b82f6);
    cursor: pointer;
}

/* ── Currency ── */
.prm-field__currency-wrap {
    position: relative;
    display: flex;
    align-items: center;
}

.prm-field__currency-symbol {
    position: absolute;
    left: 12px;
    color: var(--prm-text-secondary, #6b7280);
    font-size: 0.9375rem;
    pointer-events: none;
}

.prm-field__input--currency {
    padding-left: 28px;
}

/* ── Color ── */
.prm-field__color-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
}

.prm-field__color-input {
    width: 40px;
    height: 40px;
    padding: 2px;
    border: 1px solid var(--prm-border-input, #6B7280);
    border-radius: var(--prm-radius, 6px);
    background: var(--prm-surface, #ffffff);
    cursor: pointer;
}

.prm-field__color-value {
    font-family: monospace;
    font-size: 0.875rem;
    color: var(--prm-text-secondary, #6b7280);
}

/* ── Reference ── */
.prm-field__clear:focus-visible {
    outline: 2px solid var(--prm-focus);
    outline-offset: 2px;
}

.prm-field__reference {
    position: relative;
}

.prm-field__reference-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
}

.prm-field__reference-input-wrap .prm-field__input:not([readonly]):not(:disabled) {
    background: var(--prm-surface, #ffffff);
}

.prm-field__reference-clear {
    position: absolute;
    right: 8px;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 1.2rem;
    color: var(--prm-text-secondary, #6b7280);
    padding: 4px;
    line-height: 1;
}

.prm-field__reference-clear:hover {
    color: var(--prm-error, #ef4444);
}

.prm-field__reference-dropdown {
    position: absolute;
    z-index: 100;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 200px;
    overflow-y: auto;
    background: var(--prm-surface-raised, #ffffff);
    border: 1px solid var(--prm-border-input, #6B7280);
    border-radius: var(--prm-radius, 6px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    margin-top: 4px;
}

.prm-field__reference-option {
    padding: 10px 12px;
    cursor: pointer;
    font-size: 0.9375rem;
    color: var(--prm-text-primary, #1f2937);
    transition: background 0.15s;
}

.prm-field__reference-option:hover {
    background: var(--prm-action-hover, rgba(59, 130, 246, 0.08));
}

.prm-field__reference-loading {
    padding: 10px 12px;
    color: var(--prm-text-secondary, #6b7280);
    font-size: 0.875rem;
}

/* ── Richtext ── */
.prm-field__richtext {
    min-height: 120px;
    border: 1px solid var(--prm-border-input, #6B7280);
    border-radius: var(--prm-radius, 6px);
    background: var(--prm-surface, #ffffff);
    color: var(--prm-text-primary, #1f2937);
}

.prm-field__richtext-wrap {
    border-color: var(--prm-border-input, #6B7280);
}

.prm-field--error .prm-field__richtext-wrap,
.prm-field--error .prm-field__richtext {
    border-color: var(--prm-error-on-surface, #B93C0E);
}

.prm-field__richtext:focus {
    border-color: var(--prm-primary, #3b82f6);
    box-shadow: 0 0 0 3px var(--prm-focus, rgba(59, 130, 246, 0.15));
    outline: none;
}

.prm-field--error .prm-field__richtext:focus {
    border-color: var(--prm-error-on-surface, #B93C0E);
}

/* ── Attachment ── */
.prm-field__attachment {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.prm-field__file-input {
    font-size: 0.875rem;
    color: var(--prm-text-primary, #1f2937);
}

.prm-field__file-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: var(--prm-surface, #f3f4f6);
    border-radius: var(--prm-radius, 6px);
    font-size: 0.875rem;
}

.prm-field__file-clear {
    border: none;
    background: none;
    cursor: pointer;
    color: var(--prm-text-secondary, #6b7280);
    font-size: 1.1rem;
}

.prm-field__file-clear:hover {
    color: var(--prm-error, #ef4444);
}

/* ── Errors & Info ── */
.prm-field__errors {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.prm-field__error {
    font-size: 0.8rem;
    color: var(--prm-error-on-surface, #B93C0E);
    line-height: 1.3;
}

.prm-field__info-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.prm-field__info {
    font-size: 0.8rem;
    color: var(--prm-info, #3b82f6);
    line-height: 1.3;
}

/* ── Form Summary ── */
.prm-form-summary {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    background: var(--prm-surface, #f9fafb);
    border-radius: var(--prm-radius, 8px);
    border: 1px solid var(--prm-border, #e5e7eb);
}

.prm-form-summary__item {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 16px;
    padding: 8px 0;
    border-bottom: 1px solid var(--prm-border, #f3f4f6);
}

.prm-form-summary__item:last-child {
    border-bottom: none;
}

.prm-form-summary__label {
    font-size: 0.875rem;
    color: var(--prm-text-secondary, #6b7280);
    font-weight: 500;
    flex-shrink: 0;
}

.prm-form-summary__value {
    font-size: 0.9375rem;
    color: var(--prm-text-primary, #1f2937);
    text-align: right;
    word-break: break-word;
}

.prm-form-summary__empty {
    text-align: center;
    padding: 24px;
    color: var(--prm-text-disabled, #9ca3af);
    font-size: 0.875rem;
}

/* ── Buttons ── */
.prm-submit-wrap {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding-top: 16px;
    border-top: 1px solid var(--prm-border, #e5e7eb);
    margin-top: 8px;
}

.prm-step-nav {
    display: flex;
    gap: 12px;
}

.prm-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 10px 24px;
    border: none;
    border-radius: var(--prm-radius, 6px);
    font-size: 0.9375rem;
    font-weight: 500;
    font-family: inherit;
    cursor: pointer;
    transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease;
    line-height: 1.4;
}

.prm-btn:active {
    transform: scale(0.98);
}

.prm-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.prm-btn--primary {
    background: var(--prm-primary, #3b82f6);
    color: var(--prm-primary-contrast, #ffffff);
}

.prm-btn--primary:hover:not(:disabled) {
    background: var(--prm-primary-dark, #2563eb);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.prm-btn--secondary {
    background: var(--prm-surface, #f3f4f6);
    color: var(--prm-text-primary, #1f2937);
    border: 1px solid var(--prm-border, #d1d5db);
}

.prm-btn--secondary:hover:not(:disabled) {
    background: var(--prm-bg, #e5e7eb);
}

/* ── Responsive ── */
@media (max-width: 640px) {
    .prm-form {
        padding: 16px;
    }

    .prm-row {
        grid-template-columns: 1fr;
    }

    .prm-stepper {
        flex-direction: column;
        gap: 8px;
    }

    .prm-stepper__step {
        padding: 6px 12px;
    }

    .prm-submit-wrap {
        flex-direction: column;
    }

    .prm-step-nav {
        flex-direction: row;
    }

    .prm-btn {
        width: 100%;
    }
}
`;
};
